/**
 * One Chroma theme document, as the web reads it: its CSS variables and its validation.
 *
 * The catalog is data (`docs/theme/theme-catalog-as-data-2026-09.md`): `shared/themes/catalog.json`
 * is exported into the bundled snapshot `web/tokens/themes.json` + `chroma.css`, and the same
 * documents are published to the object plane, where `theme.ts` fetches them at runtime. A theme
 * that exists only in the fetched catalog has no `chroma.css` block, so `applyTheme` writes the
 * variables below through CSSOM. `chroma.css` stays as the pre-JS first-paint layer; the agreement
 * test (`src/test/theme-document.test.ts`) parses it and checks that this module emits exactly the
 * same canonical --chroma-* variables for every snapshot theme, so the two paths cannot drift.
 * Web-only board surface variables are composed afterward and tested separately.
 */
import { boardSurfaceVariables } from "./board-surface.js";
import schemaTypes from "../../../tokens/schema-types.json";

type Colour = string;
type Rest = Record<string, unknown>;

/**
 * A catalog theme document (`ChromaTheme.kt`, schema v1): the fields the web reads, which
 * {@link themeDocumentErrors} checks before a fetched document is trusted. Everything else a
 * document carries (app-only fields, fields from a newer build) rides along unread.
 */
export interface ThemeDocument {
  id: string;
  name: string;
  /** Empty on a family's base (`ChromaTheme.familyId`). */
  familyId: string;
  /** Empty on a family's base. */
  variantName: string;
  schemaVersion?: number;
  colors: {
    lightSquare: Colour; darkSquare: Colour; coordinateColor: Colour; canvasBackground: Colour;
    lastMoveHighlight: Colour; checkHighlight: Colour; premoveHighlight: Colour;
    whitePieceColor: Colour; blackPieceColor: Colour;
    borderColor?: Colour | null; borderEdgeColor?: Colour | null;
  } & Rest;
  chrome: {
    backgroundPrimary: Colour; backgroundSecondary: Colour; surface: Colour; surfaceElevated: Colour;
    borderSubtle: Colour; textPrimary: Colour; textSecondary: Colour; textTertiary: Colour;
    accentPrimary: Colour; accentSecondary: Colour; liveIndicator: Colour;
  } & Rest;
  extensionConfig?: Rest;
  rendering: { boardImageName?: string | null; pieceMaterialShaderId?: string | null; boardShaderId?: string | null } & Rest;
  pieceConfig: { defaultPieceStyleId: string; pieceSetPrefix?: string | null } & Rest;
  kinematics: { moveDurationMs: number; moveEasing: string } & Rest;
  sensory: { headerFont: string; moveFont: string } & Rest;
}

/** `ChromaTheme.CURRENT_SCHEMA_VERSION`: a document from a newer schema is skipped, never half-read. */
export const THEME_SCHEMA_VERSION = 1;

/** Exactly `#RRGGBB` or `#AARRGGBB` (§2.4): no whitespace (never trimmed), no bare hex, no `#RGB`. */
export function isHexColour(value: unknown): value is string {
  return typeof value === "string" && /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value);
}

// mirror-of: shared/src/jvmMain/kotlin/com/chessstreamlive/theme/ThemeTokenExporter.kt (cssColor)
/** Chroma `#AARRGGBB`/`#RRGGBB` → CSS `#RRGGBBAA`/`#RRGGBB`, upper case. */
export function cssColor(hex: string): string {
  const raw = hex.trim().replace(/^#/, "").toUpperCase();
  if (raw.length === 8) {
    const alpha = raw.slice(0, 2);
    return alpha === "FF" ? `#${raw.slice(2)}` : `#${raw.slice(2)}${alpha}`;
  }
  return raw.length === 6 ? `#${raw}` : "#FF00FF"; // HexColorParser's magenta fallback
}

// mirror-of: shared/src/jvmMain/kotlin/com/chessstreamlive/theme/ThemeTokenExporter.kt (cssFontStack)
/** A Chroma font behaviour id → CSS font stack. */
export function cssFontStack(fontId: string): string {
  switch (fontId) {
    case "serif": return "'New York', Georgia, serif";
    case "jetbrains_mono": return "'JetBrains Mono', 'SF Mono', ui-monospace, monospace";
    case "helvetica_neue": return "'Helvetica Neue', Helvetica, Arial, sans-serif";
    default: return "system-ui, -apple-system, sans-serif";
  }
}

// mirror-of: shared/src/jvmMain/kotlin/com/chessstreamlive/theme/ThemeTokenExporter.kt (cssEasing)
/** A Chroma kinematics easing id → CSS easing. */
export function cssEasing(easingId: string): string {
  switch (easingId) {
    case "core.spring": return "cubic-bezier(0.34, 1.3, 0.64, 1)";
    case "core.snap_expo": return "cubic-bezier(0.16, 1, 0.3, 1)";
    default: return "ease-in-out";
  }
}

// mirror-of: shared/src/jvmMain/kotlin/com/chessstreamlive/theme/ThemeTokenExporter.kt (cssBlock)
/** Canonical `--chroma-*` variables in exporter order. */
function exportedThemeCssVariables(theme: ThemeDocument): [name: string, value: string][] {
  const { colors: c, chrome: ch } = theme;
  return [
    ["--chroma-light-square", cssColor(c.lightSquare)],
    ["--chroma-dark-square", cssColor(c.darkSquare)],
    ["--chroma-coordinate", cssColor(c.coordinateColor)],
    ["--chroma-canvas-background", cssColor(c.canvasBackground)],
    ["--chroma-last-move-highlight", cssColor(c.lastMoveHighlight)],
    ["--chroma-check-highlight", cssColor(c.checkHighlight)],
    ["--chroma-premove-highlight", cssColor(c.premoveHighlight)],
    ["--chroma-white-piece", cssColor(c.whitePieceColor)],
    ["--chroma-black-piece", cssColor(c.blackPieceColor)],
    ["--chroma-background-primary", cssColor(ch.backgroundPrimary)],
    ["--chroma-background-secondary", cssColor(ch.backgroundSecondary)],
    ["--chroma-surface", cssColor(ch.surface)],
    ["--chroma-surface-elevated", cssColor(ch.surfaceElevated)],
    ["--chroma-border-subtle", cssColor(ch.borderSubtle)],
    ["--chroma-text-primary", cssColor(ch.textPrimary)],
    ["--chroma-text-secondary", cssColor(ch.textSecondary)],
    ["--chroma-text-tertiary", cssColor(ch.textTertiary)],
    ["--chroma-accent-primary", cssColor(ch.accentPrimary)],
    ["--chroma-accent-secondary", cssColor(ch.accentSecondary)],
    ["--chroma-live-indicator", cssColor(ch.liveIndicator)],
    ["--chroma-header-font", cssFontStack(theme.sensory.headerFont)],
    ["--chroma-move-font", cssFontStack(theme.sensory.moveFont)],
    ["--chroma-move-duration", `${theme.kinematics.moveDurationMs}ms`],
    ["--chroma-move-easing", cssEasing(theme.kinematics.moveEasing)],
  ];
}

/** Compose canonical palette tokens with the web renderer's approved surface variables. */
export function themeCssVariables(theme: ThemeDocument): [name: string, value: string][] {
  return [...exportedThemeCssVariables(theme), ...boardSurfaceVariables(theme.rendering)];
}

// mirror-of: shared/src/commonMain/kotlin/com/chessstreamlive/theme/ChromaThemeDefaults.kt (ChromaCatalog)
// The runtime accept/reject contract is docs/theme/theme-catalog-as-data-2026-09.md §2.4, pinned by
// shared/themes/validation-fixtures.json, which the KMP, web and iOS runners all execute.
const SECTIONS = ["colors", "chrome", "rendering", "pieceConfig", "kinematics", "sensory"] as const;
/** §2.4 required colours: the board's nine (both piece colours included) and chrome's twelve. */
const REQUIRED_COLOURS = {
  colors: ["lightSquare", "darkSquare", "coordinateColor", "canvasBackground", "lastMoveHighlight",
    "checkHighlight", "premoveHighlight", "whitePieceColor", "blackPieceColor"],
  chrome: ["backgroundPrimary", "backgroundSecondary", "surface", "surfaceElevated", "borderSubtle", "textPrimary",
    "textSecondary", "textTertiary", "accentPrimary", "accentSecondary", "accentLegalMove", "liveIndicator"],
} as const;
/** §2.4 optional colours: absent or null, else valid. */
const OPTIONAL_COLOURS = {
  colors: ["borderColor", "borderEdgeColor"],
  chrome: ["surfaceSunken", "highlight", "highlightDeep", "signal", "signalDeep", "negativeDeep", "hint", "evalMistake", "evalBrilliant",
    "cardShadowColor", "elevatedShadowColor"],
} as const;
/**
 * Fields the web reads that a document may omit: an absent field takes its schema default
 * (`ChromaTheme`'s, §2.4), filled here with the same values. Its TYPE, when present, is the
 * generic schema check's business ({@link schemaTypeErrors}), not this table's.
 */
const DEFAULTED = {
  pieceConfig: { defaultPieceStyleId: "staunton" },
  kinematics: { moveEasing: "core.spring", moveDurationMs: 150 },
  sensory: { headerFont: "system", moveFont: "jetbrains_mono" },
} as const;
const ART_PREFIX = /^[a-z]{2,4}$/;

// mirror-of: shared/src/commonMain/kotlin/com/chessstreamlive/theme/ChromaSchemaTypes.kt (conforms)
/**
 * The field-type table, generated from the `ChromaTheme` serializer descriptor by
 * `:shared:exportThemeTokens` (`ChromaSchemaTypes`), so it cannot drift from the schema.
 */
interface FieldType {
  type: "int" | "number" | "string" | "bool" | "object" | "map" | "list";
  nullable: boolean;
  of?: string;
  /** An `int` row's inclusive range (Kotlin `Int`: -2^31..2^31-1); a value outside it is rejected. */
  min?: number;
  max?: number;
}
const SCHEMA_FIELDS = schemaTypes.fields as Record<string, FieldType>;

function matchesType(value: unknown, type: string): boolean {
  switch (type) {
    case "int": return typeof value === "number" && Number.isFinite(value) && Number.isInteger(value);
    case "number": return typeof value === "number" && Number.isFinite(value);
    case "string": return typeof value === "string";
    case "bool": return typeof value === "boolean";
    case "object": case "map": return isRecord(value);
    case "list": return Array.isArray(value);
    default: return false;
  }
}

function matchesField(value: unknown, field: FieldType): boolean {
  if (value === null) return field.nullable;
  if (!matchesType(value, field.type)) return false;
  if (typeof value === "number" && ((field.min !== undefined && value < field.min) || (field.max !== undefined && value > field.max))) return false;
  const of = field.of;
  if (of === undefined) return true;
  const elements = field.type === "list" ? (value as unknown[]) : Object.values(value as Record<string, unknown>);
  return elements.every((element) => element !== null && matchesType(element, of));
}

/**
 * §2.4 "Every field present has its schema type": for each table path present in the document,
 * `null` only where nullable; `int` a finite number with an integral value (`1.0` counts) inside
 * the row's inclusive `min`..`max`;
 * `number` a finite number; `string`/`bool` exactly those JSON types; `object`/`map` a plain
 * object, `list` an array, and every non-null element of a list or map of its `of` type. An
 * absent field is fine here (the §2.4 required fields are checked on their own).
 */
export function schemaTypeErrors(doc: Record<string, unknown>): string[] {
  const errors: string[] = [];
  for (const [path, field] of Object.entries(SCHEMA_FIELDS)) {
    let node: unknown = doc;
    for (const key of path.split(".")) node = isRecord(node) ? node[key] : undefined;
    if (node !== undefined && !matchesField(node, field)) errors.push(`${path}: expected ${field.nullable ? "nullable " : ""}${field.type}${field.of ? ` of ${field.of}` : ""}`);
  }
  return errors;
}

/** §2.4: blank is ASCII whitespace only (space, tab, LF, CR) — never `String.trim()`, which strips U+FEFF too. */
export const isBlank = (text: string): boolean => /^[ \t\n\r]*$/.test(text);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Why one document cannot be shown (§2.4 "Per document"), or `[]`: an object with a bare
 * snake_case id and a non-blank ({@link isBlank}) name; schemaVersion ≤ {@link THEME_SCHEMA_VERSION} (absent = 1);
 * all six sections; every required colour exactly `#RRGGBB`/`#AARRGGBB` (never trimmed); optional
 * colours absent, null or valid; `pieceConfig.pieceSetPrefix` 2–4 lower-case letters; every field
 * present of its schema type ({@link schemaTypeErrors}). Unknown fields are ignored.
 */
export function themeDocumentErrors(value: unknown): string[] {
  if (!isRecord(value)) return ["not an object"];
  const errors: string[] = [];
  const { id, name, schemaVersion } = value;
  if (typeof id !== "string" || !/^[a-z0-9_]+$/.test(id)) errors.push(`id ${JSON.stringify(id)} must be bare snake_case`);
  if (typeof name !== "string" || isBlank(name)) errors.push("name must not be blank");
  if (typeof schemaVersion === "number" && schemaVersion > THEME_SCHEMA_VERSION) {
    errors.push(`schemaVersion ${JSON.stringify(schemaVersion)} is not supported (≤ ${THEME_SCHEMA_VERSION})`);
  }
  for (const section of SECTIONS) if (!isRecord(value[section])) errors.push(`${section} is missing`);
  const at = (section: string, field: string): unknown => (isRecord(value[section]) ? value[section][field] : undefined);
  for (const [section, fields] of Object.entries(REQUIRED_COLOURS)) {
    for (const field of fields) if (!isHexColour(at(section, field))) errors.push(`${section}.${field}: invalid colour ${JSON.stringify(at(section, field))}`);
  }
  for (const [section, fields] of Object.entries(OPTIONAL_COLOURS)) {
    for (const field of fields) {
      const colour = at(section, field);
      if (colour !== undefined && colour !== null && !isHexColour(colour)) errors.push(`${section}.${field}: invalid colour ${JSON.stringify(colour)}`);
    }
  }
  const prefix = at("pieceConfig", "pieceSetPrefix");
  if (typeof prefix !== "string" || !ART_PREFIX.test(prefix)) errors.push(`pieceConfig.pieceSetPrefix ${JSON.stringify(prefix)} must be 2–4 lower-case letters`);
  errors.push(...schemaTypeErrors(value));
  return errors;
}

/** A valid document with Kotlin's defaults filled in where it omitted a field the web reads. */
function withDefaults(value: Record<string, unknown>): ThemeDocument {
  const doc: Record<string, unknown> = { ...value, familyId: value.familyId ?? "", variantName: value.variantName ?? "" };
  for (const [section, fields] of Object.entries(DEFAULTED)) {
    const current = value[section] as Record<string, unknown>;
    const filled = { ...current };
    for (const [field, fallback] of Object.entries(fields)) if (filled[field] === undefined) filled[field] = fallback;
    doc[section] = filled;
  }
  return doc as unknown as ThemeDocument;
}

/** The family a document groups under: its own id when `familyId` is empty (`ChromaTheme.family`). */
export const familyOf = (theme: Pick<ThemeDocument, "id" | "familyId">): string => theme.familyId || theme.id;

/**
 * A fetched catalog → the documents that can be shown, in catalog order (§2.4 "Catalog", in
 * order): a document failing {@link themeDocumentErrors} is skipped on its own; a duplicate id
 * keeps the first; `familyId` empty or equal to the id is a base, whose `variantName` is ignored;
 * a variant needs a non-blank `variantName` and a base accepted EARLIER — so a variant of a
 * variant, an orphan, or one listed before its base is dropped. Not an array → `[]`.
 */
export function parseThemeCatalog(body: unknown): ThemeDocument[] {
  if (!Array.isArray(body)) return [];
  const accepted = new Map<string, ThemeDocument>();
  for (const value of body) {
    if (themeDocumentErrors(value).length > 0) continue;
    const doc = withDefaults(value as Record<string, unknown>);
    if (accepted.has(doc.id)) continue;
    if (doc.familyId === "" || doc.familyId === doc.id) {
      accepted.set(doc.id, { ...doc, familyId: "", variantName: "" });
      continue;
    }
    const base = accepted.get(doc.familyId);
    if (!base || base.familyId !== "" || isBlank(doc.variantName)) continue;
    accepted.set(doc.id, doc);
  }
  return [...accepted.values()];
}
